/*
 * Infinite related products
 * ---------------------------------------------------------------------------
 * Keeps appending product cards under a product page as the shopper scrolls.
 *
 * Supply order: related recommendations -> complementary recommendations ->
 * paged cards from this product's collections -> re-lap in a new sort order.
 *
 * Loading is driven by FOUR independent triggers so it can never stall:
 *   1. an eager first load on connect
 *   2. a prefill of the first few batches regardless of scroll position
 *   3. an IntersectionObserver on the sentinel
 *   4. a throttled scroll/resize listener
 * Triggers 3 and 4 alone are not enough: if the sentinel is already on screen
 * when a batch lands it never *crosses* into view again, so no further
 * intersection events fire and the feed silently stops at one batch.
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('infinite-related-products')) return;

  var PREFILL_BATCHES = 4;

  class InfiniteRelatedProducts extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.grid = this.querySelector('[data-ir-grid]');
      this.sentinel = this.querySelector('[data-ir-sentinel]');
      this.headingEl = this.querySelector('[data-ir-heading]');
      this.statusEl = this.querySelector('[data-ir-status]');
      if (!this.grid || !this.sentinel) return;

      this.loading = false;
      this.finished = false;
      this.batches = 0;
      this.emptyStreak = 0;
      this.revealed = false;
      this.topUpTimer = null;

      this.maxBatches = parseInt(this.dataset.maxBatches || '40', 10);
      if (window.Shopify && window.Shopify.designMode) this.maxBatches = 2;

      this.seen = new Set();
      if (this.dataset.productHandle) this.seen.add(this.dataset.productHandle);

      this.recStage = 0;
      this.sources = (this.dataset.feedSources || 'all')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      if (!this.sources.length) this.sources = ['all'];

      this.sorts = (this.dataset.feedSorts || 'best-selling,created-descending,price-ascending,title-ascending,price-descending,created-ascending')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      this.sourceIndex = 0;
      this.sortIndex = 0;
      this.page = 1;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect.bind(this), { rootMargin: '900px 0px' });
        this.observer.observe(this.sentinel);
      }

      this.onScroll = function () { self.topUp(); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.loadNext();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.topUpTimer) clearTimeout(this.topUpTimer);
    }

    onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { this.loadNext(); break; }
      }
    }

    topUp() {
      var self = this;
      if (this.finished) return;
      if (this.topUpTimer) clearTimeout(this.topUpTimer);
      this.topUpTimer = setTimeout(function () {
        if (self.finished || self.loading) return;

        // Prefill: for the first few batches, load regardless of where the
        // shopper is on the page. Guarantees a full-looking grid even if no
        // scroll or intersection event ever arrives.
        if (self.batches < PREFILL_BATCHES) { self.loadNext(); return; }

        var rect = self.sentinel.getBoundingClientRect();
        if (rect.top < window.innerHeight + 600) self.loadNext();
      }, 60);
    }

    nextUrl() {
      var sid = encodeURIComponent(this.dataset.sectionId || '');
      var pid = encodeURIComponent(this.dataset.productId || '');
      var limit = encodeURIComponent(this.dataset.recLimit || '8');
      var recUrl = this.dataset.recommendationsUrl || '/recommendations/products';

      if (this.recStage === 0) {
        this.recStage = 1;
        return recUrl + '?section_id=' + sid + '&product_id=' + pid + '&limit=' + limit + '&intent=related';
      }
      if (this.recStage === 1) {
        this.recStage = 2;
        return recUrl + '?section_id=' + sid + '&product_id=' + pid + '&limit=' + limit + '&intent=complementary';
      }

      var base = this.dataset.collectionsUrl || '/collections';
      var handle = this.sources[this.sourceIndex];
      var sort = this.sorts[this.sortIndex];
      return base + '/' + encodeURIComponent(handle) +
        '?section_id=' + encodeURIComponent(this.dataset.feedSection || 'infinite-related-feed') +
        '&page=' + this.page + '&sort_by=' + encodeURIComponent(sort);
    }

    advance(gotItems) {
      if (this.recStage < 2) return;
      if (gotItems) { this.page += 1; this.emptyStreak = 0; return; }

      this.emptyStreak += 1;
      this.page = 1;
      this.sourceIndex += 1;

      if (this.sourceIndex >= this.sources.length) {
        this.sourceIndex = 0;
        this.sortIndex += 1;
        this.seen = new Set(this.dataset.productHandle ? [this.dataset.productHandle] : []);
        if (this.sortIndex >= this.sorts.length) this.sortIndex = 0;
      }

      if (this.emptyStreak > this.sources.length * this.sorts.length + 2) this.finish();
    }

    append(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var items = doc.querySelectorAll('[data-ir-item]');
      if (!items.length) return 0;

      var frag = document.createDocumentFragment();
      var added = 0;
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        var handle = li.getAttribute('data-ir-handle');
        if (handle && this.seen.has(handle)) continue;
        if (handle) this.seen.add(handle);
        li.removeAttribute('data-ir-item');
        frag.appendChild(li);
        added += 1;
      }
      if (added) this.grid.appendChild(frag);
      return added;
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      if (this.headingEl) this.headingEl.removeAttribute('hidden');
      this.classList.add('is-populated');
    }

    finish() {
      this.finished = true;
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      this.sentinel.setAttribute('hidden', '');
    }

    loadNext() {
      var self = this;
      if (this.loading || this.finished) return;
      if (this.maxBatches > 0 && this.batches >= this.maxBatches) { this.finish(); return; }

      this.loading = true;
      this.sentinel.classList.add('is-loading');

      fetch(this.nextUrl())
        .then(function (res) { return res.ok ? res.text() : ''; })
        .catch(function () { return ''; })
        .then(function (text) {
          var added = text ? self.append(text) : 0;
          self.batches += 1;
          self.advance(added > 0);
          if (added > 0) self.reveal();
          self.loading = false;
          self.sentinel.classList.remove('is-loading');
          if (self.finished) return;

          // A batch that added nothing (e.g. no complementary products) must
          // not stall the chain - go straight to the next source.
          if (added === 0) { self.loadNext(); return; }
          self.topUp();
        });
    }
  }

  customElements.define('infinite-related-products', InfiniteRelatedProducts);
})();
